import type { AxiosError } from 'axios';

/**
 * h6-3 — welke melding hoort bij een mislukte indiening.
 *
 * Elke fout uit de keten draagt een eigen code; een tekst van CustomerPortal komt hier in de regel
 * nooit doorheen (FR48). Dat is niet alleen een taalkwestie: hun meldingen gaan over hún begrippen
 * en zeggen de klant niets over wat híj moet doen.
 *
 * **h7-3 breekt die regel op één plek open, met opzet.** De marketingimport van CustomerPortal kent
 * geen codes en geen sleutels; alles wat hij over een regel te zeggen heeft staat in vrije Engelse
 * tekst. De keuze is daar dus niet "code of tekst" maar "tekst of niets". De negen teksten die die
 * bron kan opleveren krijgen aan de serverkant onze eigen sleutel met een nette vertaling; een
 * tekst die wij niet herkennen komt door onder `intake.submit.error.portalUnknown` en wordt op het
 * scherm als citaat van het ontvangende systeem getoond. Niets daarvan omzeilt de voorvoegseleis
 * hieronder — het zijn gewone sleutels van ons, alleen met hún tekst als waarde.
 *
 * Het onderscheid dat telt is "u kunt er iets aan doen" tegenover "het lag aan ons". Bij het eerste
 * hoort een aanwijzing, bij het tweede een uitnodiging om het straks opnieuw te proberen — en in
 * beide gevallen staat de aanmelding gewoon weer klaar.
 */
const CODE_NAAR_SLEUTEL: Readonly<Record<string, string>> = {
  INTAKE_SUBMIT_FILE_REJECTED: 'intake.review.submitBar.errorFileRejected',
  INTAKE_SUBMIT_CONTENT_REJECTED: 'intake.review.submitBar.errorContentRejected',
  INTAKE_SUBMIT_CHAIN_UNAVAILABLE: 'intake.review.submitBar.errorChainUnavailable',
  INTAKE_SUBMIT_NOT_COMPLETED: 'intake.review.submitBar.errorSubmitNotCompleted',
  INTAKE_SUBMIT_FILE_MISSING: 'intake.review.submitBar.errorFileMissing',
  INTAKE_SUBMIT_UNCONFIRMED: 'intake.review.submitBar.errorSubmitUnconfirmed',
};

export function submitFoutSleutel(fout: unknown): string {
  const antwoord = (fout as AxiosError<{ error?: { code?: string } }> | null)?.response;
  const code = antwoord?.data?.error?.code;

  // Een botsing op het versienummer: iemand anders heeft de sessie gewijzigd, of deze indiening is
  // al onderweg. De klant hoeft niet te weten wélke van de twee — hij moet weten dat het scherm
  // achterloopt en dat verversen genoeg is.
  if (antwoord?.status === 409 && !CODE_NAAR_SLEUTEL[code ?? ""]) return 'intake.review.submitBar.errorConflict';

  // Een onbekende code is geen reden om een rauwe sleutel te tonen; dan is de algemene melding beter.
  return (code && CODE_NAAR_SLEUTEL[code]) || 'intake.review.submitBar.error';
}

/**
 * H13-9 — één melding per regel uit een geweigerd bestand.
 *
 * De sleutels zijn van ons (FR48); de waarden komen uit het bestand van de klant zelf. Er wordt
 * niets van bewaard: na herladen staat alleen de foutcode nog op de sessie, en dan belooft de
 * melding deze regels ook niet meer.
 */
export interface SubmitProbleem {
  key: string;
  row?: number;
  value?: string;
  /** De veldnaam als sleutel; het scherm vertaalt hem via `intake.submit.measurement.*`. */
  field?: string;
  shown?: string;
}

/** Elke meldingsleutel begint hiermee; alles daarbuiten is niet van ons en wordt niet getoond. */
const PROBLEEM_SLEUTEL_PREFIX = 'intake.submit.error.';

export function submitFoutProblemen(fout: unknown): SubmitProbleem[] {
  const problemen = (fout as AxiosError<{ error?: { details?: { problemen?: unknown } } }> | null)?.response?.data?.error
    ?.details?.problemen;
  return eigenMeldingen(problemen);
}

/**
 * h7-4 — dezelfde zeef voor de waarschuwingen van een GESLAAGDE indiening.
 *
 * Die komen langs een andere weg binnen (`submitWarnings` op het antwoord in plaats van `details`
 * op een fout), maar zijn even hard een antwoordlichaam waarvan de vorm niet vastligt. Zonder deze
 * zeef belandt een sleutel die de vertaalbestanden niet kennen als rauwe tekst op het scherm.
 */
export function submitWaarschuwingen(warnings: unknown): SubmitProbleem[] {
  return eigenMeldingen(warnings);
}

function eigenMeldingen(problemen: unknown): SubmitProbleem[] {
  if (!Array.isArray(problemen)) return [];
  // Wat hier binnenkomt is een antwoordlichaam, niet iets waarvan de vorm vaststaat. Een element
  // zonder `key` laat het scherm gooien tijdens het renderen — en dan valt niet één melding weg
  // maar het hele controlescherm. De voorvoegseleis houdt bovendien een óudersleutel buiten de deur:
  // `intake.submit.error` bestáát als tak, vertaalt naar niets bruikbaars en zou als rauwe tekst op
  // het scherm belanden.
  return (problemen as unknown[]).filter(
    (probleem): probleem is SubmitProbleem =>
      typeof (probleem as SubmitProbleem | null)?.key === 'string' &&
      (probleem as SubmitProbleem).key.startsWith(PROBLEEM_SLEUTEL_PREFIX),
  );
}
